import React, { useMemo } from "react";
import { Icon, Popup, Table } from "semantic-ui-react";
import i18nstring, { strings } from "../../i18n";
import SectionHeading from "./SectionHeading";
import { Button } from "../atoms/Button";
import "./MoleculeStyles.scss";

const tableStyle = {
  fontFamily: "Lato, sans-serif",
  borderCollapse: "collapse"
};

const formatAddress = (address) => {
  if (!address) return "";
  const parts = [address.streetAddress1, address.city, address.state, address.zip].filter(Boolean);
  return parts.join(", ");
};

const formatHoursSummary = (schedule, dayLabels, emptyLabel) => {
  if (!schedule?.deliverySchedule) return emptyLabel;

  const { AM = [], PM = [] } = schedule.deliverySchedule;
  const entries = [];

  AM.forEach((day) => entries.push(`${dayLabels[day] || day} AM`));
  PM.forEach((day) => entries.push(`${dayLabels[day] || day} PM`));

  return entries.length > 0 ? entries.join(", ") : emptyLabel;
};

export default function LocationsTable({ loadedAddresses, loadedSchedules, onEdit, onDelete, onCreate }) {
  const dayLabels = useMemo(() => i18nstring(strings.DAY_LABELS) || {}, []);
  const emptyHoursLabel = useMemo(
    () => i18nstring(strings.LOCATIONS_NO_HOURS),
    []
  );
  const scheduleById = useMemo(
    () => new Map((loadedSchedules || []).map((schedule) => [schedule.id, schedule])),
    [loadedSchedules]
  );

  const handleCreateClick = () => {
    onCreate?.();
  };

  const getScheduleForAddress = (address) => {
    if (!address.schedule) return null;
    return scheduleById.get(address.schedule) || null;
  };

  return (
    <>
      <div className="locations_table_layout">
        <div className="locations_table_sidebar">
          <SectionHeading title={i18nstring(strings.LOCATIONS_HEADING)} />
        </div>
        <div className="locations_table_divider" />
        <div className="locations_table_content">
          <Table celled style={tableStyle}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2}>{i18nstring(strings.LOCATIONS_COL_NAME)}</Table.HeaderCell>
              <Table.HeaderCell width={4}>{i18nstring(strings.LOCATIONS_COL_ADDRESS)}</Table.HeaderCell>
              <Table.HeaderCell width={5}>{i18nstring(strings.LOCATIONS_COL_HOURS_SUMMARY)}</Table.HeaderCell>
              <Table.HeaderCell width={2}>{i18nstring(strings.LOCATIONS_COL_ZIPS_SERVED)}</Table.HeaderCell>
              <Table.HeaderCell width={1} textAlign="center">{i18nstring(strings.LOCATIONS_COL_ACTIONS)}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loadedAddresses.map((address) => {
              const schedule = getScheduleForAddress(address);
              const zips = Array.isArray(address.zips) && address.zips.length > 0
                ? address.zips
                :[];
              const firstZip = zips[0];
              const extraCount = zips.length - 1;
                return (
                  <Table.Row key={address.key}>
                    <Table.Cell>
                      {address.title || address.name || ""}
                    </Table.Cell>
                    <Table.Cell>{formatAddress(address.address)}</Table.Cell>
                    <Table.Cell>
                      {formatHoursSummary(schedule, dayLabels, emptyHoursLabel)}
                    </Table.Cell>
                    <Table.Cell>
                      {firstZip ? (
                        <div className="locations_zips_cell">
                          <span>{firstZip}</span>
                          {extraCount > 0 && (
                            <Popup
                              trigger={
                                <span className="locations_zips_more">+{extraCount} more</span>
                              }
                              position="top center"
                              on="click"
                              className="locations_zips_popup_container"
                            >
                              <div className="locations_zips_popup">
                                {zips.join(", ")}
                              </div>
                            </Popup>
                          )}
                        </div>
                      ) : (
                        <span className="locations_zips_none">{i18nstring(strings.MANAGE_ADDRESSES_NO_ZIPS)}</span>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <div className="location_buttons_container"
                        onClick={e => e.stopPropagation()}
                      >
                        <Icon
                          name="edit"
                          color="blue"
                          onClick={() => onEdit(address)}
                          style={{ cursor: "pointer" }}
                        />
                        {address.key !== 0 && address.key !== -1 && (
                          <Icon
                            name="trash"
                            color="red"
                            onClick={() => onDelete(address)}
                            style={{ cursor: "pointer" }}
                          />
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
            })}
          </Table.Body>
          </Table>
        </div>
      </div>

      {onCreate && (
        <div className="locations_table_layout locations_table_layout_no_border">
          <div className="locations_table_sidebar" />
          <div className="locations_table_divider" />
          <div className="locations_table_content">
            <div className="locations_create_button_row">
              <Button
                className="locations_create_button"
                onClick={handleCreateClick}
              >
                <Icon name="plus" />
                {i18nstring(strings.CREATE_ADDRESS)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
